import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Platform,
  TextInputProps,
  TouchableOpacity,
  View
} from 'react-native';
import styled from 'styled-components/native';
import { isAddressComplete } from './isAddressComplete';
import Colors from '../constants/Colors';
import { useHttpFetchApi } from '../hooks/http/useHttpFetch';
import { UserAddress } from '../models/User';

export interface PlaceSuggestion {
  placeId: string;
  description: string;
}

export const formatAddressDisplay = (address?: UserAddress | null): string => {
  if (!address?.line1) return '';
  return [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postalCode
  ]
    .filter(Boolean)
    .join(', ');
};

export const toFormAddress = (
  address: Partial<UserAddress> | null | undefined
): UserAddress | null => {
  if (!address || !(address.line1 || '').trim()) return null;
  const country = (address.country || '').trim().toUpperCase();
  return {
    line1: (address.line1 || '').trim(),
    line2: address.line2?.trim() || '',
    city: address.city?.trim() || '',
    state: (address.state || '').trim(),
    postalCode: address.postalCode?.trim() || '',
    ...(country ? { country } : {})
  };
};

const newSessionToken = (): string =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const MIN_AUTOCOMPLETE_CHARS = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 400;
const BLUR_COMMIT_DELAY_MS = 250;
const WEB_SINGLE_LINE_HEIGHT = 62;

const InputRow = styled.View`
  position: relative;
  width: 100%;
`;

const StyledTextInput = styled.TextInput<{ empty?: boolean }>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 16px;
  line-height: 22px;
  background-color: #fff;
  border: 1px solid ${Colors.formInputBorder};
  border-radius: 8px;
  padding: ${Platform.OS === 'ios' || Platform.OS === 'web'
    ? '20px 16px'
    : '16px'};
  padding-right: 44px;
  overflow: hidden;
  ${Platform.OS === 'web'
    ? `min-height: ${WEB_SINGLE_LINE_HEIGHT}px; resize: none;`
    : ''}
` as React.FC<TextInputProps & { empty?: boolean; ref?: any }>;

const Spinner = styled.View`
  position: absolute;
  right: 14px;
  top: 18px;
`;

const Suggestions = styled.View`
  margin-top: 4px;
  background-color: #fff;
  border: 1px solid ${Colors.formInputBorder};
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
`;

const SuggestionRow = styled(TouchableOpacity)`
  padding: 14px 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${Colors.formInputBorder};
`;

const SuggestionText = styled.Text`
  font-family: 'WorkSans-Regular';
  font-size: 15px;
  line-height: 20px;
  color: ${Colors.darkText};
`;

const ErrorText = styled.Text`
  font-family: 'WorkSans-Regular';
  font-size: 13px;
  color: ${Colors.error};
  margin-top: 6px;
`;

const PICK_FROM_LIST_MESSAGE =
  'Please select an address from the provided suggestions.';
const VERIFY_FAILED_MESSAGE =
  'Could not verify that address. Please try another suggestion.';

const normalizeSuggestionText = (text: string): string =>
  text.trim().toLowerCase().replace(/\s+/g, ' ');

const US_ZIP = /^\d{5}(?:-\d{4})?$/;

/** Inverse of formatAddressDisplay for a complete US address. */
const parseFormattedAddress = (text: string): UserAddress | null => {
  const parts = text
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length < 4) return null;
  const postalCode = parts[parts.length - 1];
  if (!US_ZIP.test(postalCode)) return null;
  const state = parts[parts.length - 2];
  const city = parts[parts.length - 3];
  const streetParts = parts.slice(0, parts.length - 3);
  const line1 = streetParts[0];
  if (!line1 || !city || !state) return null;
  return {
    line1,
    line2: streetParts.slice(1).join(', '),
    city,
    state,
    postalCode,
    country: 'US'
  };
};

const suggestionStreet = (description: string): string =>
  normalizeSuggestionText(description.split(',')[0] || '');

const findExactSuggestion = (
  query: string,
  suggestions: PlaceSuggestion[]
): PlaceSuggestion | undefined => {
  const normalized = normalizeSuggestionText(query);
  if (!normalized) return undefined;
  return suggestions.find(
    suggestion => normalizeSuggestionText(suggestion.description) === normalized
  );
};

const findMatchingSuggestion = (
  query: string,
  suggestions: PlaceSuggestion[]
): PlaceSuggestion | undefined => {
  const exact = findExactSuggestion(query, suggestions);
  if (exact) return exact;

  const parsed = parseFormattedAddress(query);
  const street = normalizeSuggestionText(
    parsed?.line1 || query.split(',')[0] || ''
  );
  if (!street) return undefined;

  const streetMatches = suggestions.filter(
    suggestion => suggestionStreet(suggestion.description) === street
  );
  if (streetMatches.length === 0) return undefined;
  if (streetMatches.length === 1) return streetMatches[0];

  const city = normalizeSuggestionText(parsed?.city || '');
  if (!city) return undefined;
  const cityMatches = streetMatches.filter(suggestion =>
    suggestion.description
      .split(',')
      .map(part => normalizeSuggestionText(part))
      .includes(city)
  );
  if (cityMatches.length === 0) return undefined;
  if (cityMatches.length === 1) return cityMatches[0];

  const zip5 = (parsed?.postalCode || '').slice(0, 5);
  if (!zip5) return undefined;
  const zipMatches = cityMatches.filter(suggestion => {
    const zipInDescription = suggestion.description.match(
      /\b(\d{5})(?:-\d{4})?\b/
    );
    return zipInDescription?.[1] === zip5;
  });
  return zipMatches.length === 1 ? zipMatches[0] : undefined;
};

export type AddressAutocompleteInputProps = {
  value?: UserAddress | null;
  disabled?: boolean;
  placeholder?: string;
  onChange: (address: UserAddress | null) => void | Promise<void>;
};

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  disabled,
  placeholder = '',
  onChange
}) => {
  const { postJson } = useHttpFetchApi();
  const sessionTokenRef = useRef(newSessionToken());
  const requestSeqRef = useRef(0);
  const lastAutocompleteQueryRef = useRef('');
  const suppressAutocompleteRef = useRef(false);
  const selectingRef = useRef(false);
  const submittingRef = useRef(false);
  const submitOpRef = useRef(0);
  const focusedRef = useRef(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputValueRef = useRef('');
  const inputNodeRef = useRef<any>(null);
  const optionsRef = useRef<PlaceSuggestion[]>([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const committedDisplay = useMemo(() => formatAddressDisplay(value), [value]);
  const [inputValue, setInputValue] = useState(committedDisplay);
  const [debouncedInput, setDebouncedInput] = useState(committedDisplay);
  const [focused, setFocused] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [options, setOptions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inputHeight, setInputHeight] = useState<number | undefined>();

  inputValueRef.current = inputValue;
  optionsRef.current = options;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = inputNodeRef.current;
    const textarea =
      node?.tagName === 'TEXTAREA'
        ? node
        : node?._node || node?.getNativeRef?.();
    if (!textarea || typeof textarea.scrollHeight !== 'number') return;
    if (!(inputValue || '').trim()) {
      setInputHeight(WEB_SINGLE_LINE_HEIGHT);
      return;
    }
    const previousHeight = textarea.style.height;
    textarea.style.height = 'auto';
    const nextHeight = Math.ceil(textarea.scrollHeight);
    textarea.style.height = previousHeight;
    if (nextHeight > 0) {
      setInputHeight(Math.max(WEB_SINGLE_LINE_HEIGHT, nextHeight));
    }
  }, [inputValue]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current != null) clearTimeout(blurTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (
      !focused &&
      !selectingRef.current &&
      !resolving &&
      !submittingRef.current
    ) {
      const next = formatAddressDisplay(value);
      setInputValue(prev => (prev === next ? prev : next));
    }
  }, [value, focused, resolving]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const query = (debouncedInput || '').trim();
    const currentDisplay = formatAddressDisplay(value);
    if (
      submittingRef.current ||
      selectingRef.current ||
      suppressAutocompleteRef.current
    ) {
      requestSeqRef.current += 1;
      setLoading(false);
      return;
    }
    if (
      !focused ||
      !query ||
      query.length < MIN_AUTOCOMPLETE_CHARS ||
      query === currentDisplay
    ) {
      requestSeqRef.current += 1;
      setOptions([]);
      setListOpen(false);
      setLoading(false);
      return;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    (async () => {
      try {
        const result = await postJson<{ suggestions: PlaceSuggestion[] }>(
          'geolocation/address/autocomplete',
          {
            input: query,
            sessionToken: sessionTokenRef.current
          }
        );
        if (seq !== requestSeqRef.current) return;
        lastAutocompleteQueryRef.current = query;
        const next = result?.suggestions || [];
        setOptions(next);
        if (
          next.length &&
          focusedRef.current &&
          !suppressAutocompleteRef.current
        ) {
          setListOpen(true);
        }
      } catch {
        if (seq !== requestSeqRef.current) return;
        setOptions([]);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    })();
  }, [debouncedInput, value, focused, postJson]);

  const commit = useCallback(async (next: UserAddress | null) => {
    setErrorMessage(null);
    await Promise.resolve(onChangeRef.current(next));
  }, []);

  const resolveSuggestion = useCallback(
    async (suggestion: PlaceSuggestion) => {
      if (blurTimerRef.current != null) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      selectingRef.current = true;
      suppressAutocompleteRef.current = true;
      requestSeqRef.current += 1;
      setResolving(true);
      setErrorMessage(null);
      setListOpen(false);
      setOptions([]);
      setLoading(false);
      try {
        const placeId = suggestion.placeId.replace(/^places\//, '');
        const result = await postJson<{ address?: UserAddress }>(
          'geolocation/address/place-details',
          {
            placeId,
            sessionToken: sessionTokenRef.current
          }
        );
        sessionTokenRef.current = newSessionToken();
        lastAutocompleteQueryRef.current = '';
        const next = toFormAddress(result?.address);
        if (next && isAddressComplete(next)) {
          const display = formatAddressDisplay(next);
          setInputValue(display);
          setDebouncedInput(display);
          await commit(next);
          focusedRef.current = false;
          setFocused(false);
          Keyboard.dismiss();
        } else {
          suppressAutocompleteRef.current = false;
          setInputValue(formatAddressDisplay(value));
          setErrorMessage(
            'Could not verify that address. Please try another suggestion.'
          );
        }
      } catch {
        suppressAutocompleteRef.current = false;
        setInputValue(formatAddressDisplay(value));
        setErrorMessage(
          'Could not verify that address. Please try another suggestion.'
        );
      } finally {
        setResolving(false);
        selectingRef.current = false;
        setListOpen(false);
      }
    },
    [commit, postJson, value]
  );

  const commitFreeform = useCallback(async () => {
    if (selectingRef.current || submittingRef.current) return;
    submittingRef.current = true;
    const op = ++submitOpRef.current;
    try {
      const trimmed = (inputValueRef.current || '').trim();
      if (!trimmed) {
        await commit(null);
        return;
      }
      if (trimmed === formatAddressDisplay(value) && isAddressComplete(value)) {
        return;
      }

      const existingMatch = findMatchingSuggestion(trimmed, optionsRef.current);
      if (existingMatch) {
        await resolveSuggestion(existingMatch);
        return;
      }

      if (trimmed.length < MIN_AUTOCOMPLETE_CHARS) {
        setInputValue(formatAddressDisplay(value));
        setErrorMessage(PICK_FROM_LIST_MESSAGE);
        return;
      }

      const seq = ++requestSeqRef.current;
      setLoading(true);
      const parsed = parseFormattedAddress(trimmed);
      try {
        if (parsed && isAddressComplete(parsed)) {
          const result = await postJson<{
            verdict?: string;
            normalized?: UserAddress;
            transient?: boolean;
          }>('geolocation/address/validate', {
            line1: parsed.line1,
            line2: parsed.line2 || undefined,
            city: parsed.city,
            state: parsed.state,
            postalCode: parsed.postalCode,
            country: parsed.country
          });
          if (seq !== requestSeqRef.current) return;
          const next =
            toFormAddress(result?.normalized) || toFormAddress(parsed);
          if (
            next &&
            isAddressComplete(next) &&
            (result?.verdict === 'confirmed' ||
              result?.verdict === 'corrected' ||
              result?.transient)
          ) {
            setInputValue(formatAddressDisplay(next));
            setDebouncedInput(formatAddressDisplay(next));
            suppressAutocompleteRef.current = true;
            await commit(next);
            focusedRef.current = false;
            setFocused(false);
            setListOpen(false);
            Keyboard.dismiss();
            return;
          }
          setInputValue(formatAddressDisplay(value));
          setErrorMessage(VERIFY_FAILED_MESSAGE);
          return;
        }

        const alreadyFetched =
          normalizeSuggestionText(lastAutocompleteQueryRef.current) ===
          normalizeSuggestionText(trimmed);
        if (!alreadyFetched) {
          const result = await postJson<{ suggestions: PlaceSuggestion[] }>(
            'geolocation/address/autocomplete',
            {
              input: trimmed,
              sessionToken: sessionTokenRef.current
            }
          );
          if (seq !== requestSeqRef.current) return;
          lastAutocompleteQueryRef.current = trimmed;
          const match = findMatchingSuggestion(
            trimmed,
            result?.suggestions || []
          );
          if (match) {
            await resolveSuggestion(match);
            return;
          }
        }

        setInputValue(formatAddressDisplay(value));
        setErrorMessage(PICK_FROM_LIST_MESSAGE);
      } catch {
        if (seq !== requestSeqRef.current) return;
        setInputValue(formatAddressDisplay(value));
        setErrorMessage(PICK_FROM_LIST_MESSAGE);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    } finally {
      if (op === submitOpRef.current) submittingRef.current = false;
    }
  }, [commit, postJson, resolveSuggestion, value]);

  const handleBlur = useCallback(() => {
    if (blurTimerRef.current != null) clearTimeout(blurTimerRef.current);
    focusedRef.current = false;
    blurTimerRef.current = setTimeout(() => {
      if (selectingRef.current) return;
      void (async () => {
        await commitFreeform();
        if (focusedRef.current) return;
        setFocused(false);
        setListOpen(false);
        setOptions([]);
      })();
    }, BLUR_COMMIT_DELAY_MS);
  }, [commitFreeform]);

  const showSpinner = loading || resolving;
  const showSuggestions = listOpen && options.length > 0 && !resolving;

  return (
    <View>
      <InputRow>
        <StyledTextInput
          ref={node => {
            inputNodeRef.current = node;
          }}
          value={inputValue}
          empty={!inputValue}
          editable={!disabled && !resolving}
          placeholder={placeholder}
          placeholderTextColor={Colors.placeholderColor}
          autoCorrect={false}
          autoCapitalize='words'
          multiline
          {...(Platform.OS === 'web' ? { numberOfLines: 1 } : {})}
          textAlignVertical='top'
          blurOnSubmit
          scrollEnabled={false}
          returnKeyType='done'
          style={
            Platform.OS === 'web'
              ? { height: inputHeight || WEB_SINGLE_LINE_HEIGHT }
              : inputValue && inputHeight
                ? { minHeight: inputHeight }
                : undefined
          }
          onKeyPress={event => {
            if (Platform.OS !== 'web') return;
            const key =
              (event as { key?: string }).key || event.nativeEvent?.key;
            if (key === 'Enter') event.preventDefault?.();
          }}
          onContentSizeChange={event => {
            if (!(inputValueRef.current || '').trim()) {
              if (Platform.OS === 'web') {
                setInputHeight(WEB_SINGLE_LINE_HEIGHT);
              } else {
                setInputHeight(undefined);
              }
              return;
            }
            const nextHeight = Math.ceil(event.nativeEvent.contentSize.height);
            if (nextHeight > 0) {
              setInputHeight(
                Platform.OS === 'web'
                  ? Math.max(WEB_SINGLE_LINE_HEIGHT, nextHeight)
                  : nextHeight
              );
            }
          }}
          onFocus={() => {
            if (blurTimerRef.current != null) {
              clearTimeout(blurTimerRef.current);
              blurTimerRef.current = null;
            }
            focusedRef.current = true;
            setFocused(true);
          }}
          onChangeText={text => {
            suppressAutocompleteRef.current = false;
            setErrorMessage(null);
            setInputValue(text.replace(/[\r\n]+/g, ' '));
            setListOpen(true);
          }}
          onSubmitEditing={() => {
            if (blurTimerRef.current != null) {
              clearTimeout(blurTimerRef.current);
              blurTimerRef.current = null;
            }
            void commitFreeform();
          }}
          onBlur={handleBlur}
        />
        {showSpinner && (
          <Spinner>
            <ActivityIndicator size='small' color={Colors.mediumText} />
          </Spinner>
        )}
      </InputRow>
      {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      {showSuggestions && (
        <Suggestions>
          {options.map((option, index) => (
            <SuggestionRow
              key={option.placeId}
              activeOpacity={0.7}
              onPressIn={() => {
                // Cancel deferred blur before the list can lose the press.
                selectingRef.current = true;
                if (blurTimerRef.current != null) {
                  clearTimeout(blurTimerRef.current);
                  blurTimerRef.current = null;
                }
              }}
              onPress={() => resolveSuggestion(option)}
              style={
                index === options.length - 1
                  ? { borderBottomWidth: 0 }
                  : undefined
              }
            >
              <SuggestionText>{option.description}</SuggestionText>
            </SuggestionRow>
          ))}
        </Suggestions>
      )}
    </View>
  );
};

export default AddressAutocompleteInput;
